import { createListenerMiddleware, createSlice, PayloadAction as PA } from '@reduxjs/toolkit';

import { logWarn } from '@/api/utils/logger';
import lang from '@/lang';
import { injectFont } from '@/scss/fonts';
import { RootState } from '@/store';
import { CUSTOM_CSS_DOM_ID } from '@/utils/constants';
import { cloneDeep, mergeDeep, xssEscape } from '@/utils/lodash';
import { logDebug } from '@/utils/loggers';
import {
  LangMapKey,
  ShortNameMapKey,
  SortRuleMapKey,
  DisplayModeMapKey,
  DisplayContentMapKey,
  TickerAlignMapKey,
  TickerMapKey,
  BottomDispMapKey,
  FontFamilyMapKey,
  LayoutModeMapKey,
  MAP_FONT_FAMILY,
} from '@/utils/maps';
import { getAsyncLSSetter, getLS } from '@/utils/storage';

import { cleanMockData } from './api';

let lazyMocker: typeof import('@/utils/mocker') | null = null;

interface SortSettings {
  key: SortRuleMapKey;
  rule: -1 | 1;
}
interface DispContentSettings {
  left: DisplayContentMapKey;
  right: DisplayContentMapKey;
}
interface TickerSettings {
  top: TickerMapKey;
  bottom: TickerMapKey;
}
interface TickerAlignSettings {
  top: TickerAlignMapKey;
  bottom: TickerAlignMapKey;
}
interface FontSettings {
  family: FontFamilyMapKey;
  weight: number;
}

export interface Settings {
  // data
  sort: SortSettings;
  playerLimit: number; // combatant limit
  playerPerRow: number;
  showLB: boolean;
  youName: string; // which to represent as 'YOU'
  petMergeID: string; // merge pet data when using global client with cn language patch
  shortNumber: boolean;
  // display
  dispMode: DisplayModeMapKey;
  dispContent: DispContentSettings;
  hlYou: boolean;
  ticker: TickerSettings;
  tickerAlign: TickerAlignSettings;
  bottomDisp: BottomDispMapKey;
  shortName: ShortNameMapKey;
  // general
  mock: boolean;
  lang: LangMapKey;
  zoom: number;
  opacity: number;
  layoutMode: LayoutModeMapKey;
  fonts: FontSettings;
  customCSS: string;
}

export interface SettingsState extends Settings {
  showCombatants: boolean;
  showSettings: boolean;
  blurName: boolean;
}

const save = getAsyncLSSetter<DeepPartial<Settings>>('settings');

/** @redux initialize */

// initial settings
export const defaultSettings: Settings = {
  sort: { key: 'dps', rule: -1 },
  playerLimit: 8,
  playerPerRow: 24,
  showLB: true,
  youName: 'YOU',
  petMergeID: '',
  shortNumber: false,
  dispMode: 'single',
  dispContent: { left: 'hps', right: 'dps' },
  hlYou: true,
  ticker: { top: 'none', bottom: 'dps' },
  tickerAlign: { top: 'right', bottom: 'left' },
  bottomDisp: 'maxhit',
  shortName: 'fstlst',
  mock: false,
  lang: 'en',
  zoom: 1,
  opacity: 1,
  layoutMode: 'common',
  fonts: { family: 'default', weight: 400 },
  customCSS: '#root {}',
};
let initialState: SettingsState = {
  showCombatants: true,
  showSettings: false,
  blurName: false,
  ...cloneDeep(defaultSettings),
};

// merge saved settings into default settings
const savedSettings = getLS<DeepPartial<Settings>>('settings') || {};
try {
  logDebug('Store::Settings::mergeSavedSettings', savedSettings);
  initialState = mergeDeep(initialState, savedSettings);
} catch {
  // use default setting if saved settings is invalid
  logDebug('Store::Settings::invalidSavedSettings', savedSettings);
  initialState = { ...initialState, ...defaultSettings };
}
// reset mock every time
initialState.mock = false;

// apply initial lang
function applyLang(value: LangMapKey) {
  logDebug('Store::Settings::applyLang', value);
  document.documentElement.setAttribute('lang', value);
}
const availableLangs = Object.keys(lang);
if (!savedSettings.lang || !availableLangs.includes(savedSettings.lang)) {
  const detectedLang = navigator.language.substring(0, 2);
  if (availableLangs.includes(detectedLang)) {
    logDebug('Store::Settings::initialLangDetected', detectedLang);
    initialState.lang = detectedLang as LangMapKey;
    save({ lang: detectedLang as LangMapKey });
  }
}
applyLang(initialState.lang);

// apply initial fonts
function applyFonts(value: FontFamilyMapKey) {
  logDebug('Store::Settings::applyFonts', value);
  document.documentElement.setAttribute('data-font', value);
  injectFont(value);
}
applyFonts(initialState.fonts.family);

// apply initial font weight
function applyFontWeight(value: number) {
  let weight = value;
  if (typeof weight !== 'number' || weight < 100 || weight > 900) {
    logWarn('Store::Settings::applyFontWeight::invalidWeight', weight);
    weight = 400;
  }
  logDebug('Store::Settings::applyFontWeight', weight);
  document.documentElement.style.fontWeight = `${weight}`;
}
applyFontWeight(initialState.fonts.weight);

// apply initial zoom
function applyZoom(value: number) {
  const zoomFontSize = `${Math.floor(100 * value) || 100}px`;
  logDebug('Store::Settings::applyZoom', zoomFontSize);
  document.documentElement.style.fontSize = zoomFontSize;
}
applyZoom(initialState.zoom);

// apply initial custom style
function applyCustomCSS(value: string) {
  logDebug('Store::Settings::applyCustomCSS', value);
  const el = document.getElementById(CUSTOM_CSS_DOM_ID);
  if (el) {
    el.innerHTML = xssEscape(value);
  } else {
    const newEl = document.createElement('style');
    newEl.id = CUSTOM_CSS_DOM_ID;
    newEl.innerHTML = xssEscape(value);
    document.head.appendChild(newEl);
  }
}
applyCustomCSS(initialState.customCSS);

/** @redux slice */

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // settings container display
    toggleShowCombatants(state, { payload }: PA<boolean | undefined>) {
      logDebug('Store::Settings::toggleShowCombatants', payload);
      if (payload !== undefined) {
        state.showCombatants = payload;
      } else {
        state.showCombatants = !state.showCombatants;
      }
    },
    toggleSettings(state) {
      logDebug('Store::Settings::toggleSettings');
      state.showSettings = !state.showSettings;
    },
    toggleBlurName(state) {
      logDebug('Store::Settings::toggleBlurName');
      state.blurName = !state.blurName;
    },
    // data
    updateSort(state, { payload }: PA<Partial<SortSettings>>) {
      logDebug('Store::Settings::updateSort', payload);
      state.sort = { ...state.sort, ...payload };
      save({ sort: state.sort });
    },
    updatePlayerLimit(state, { payload }: PA<number>) {
      logDebug('Store::Settings::updatePlayerLimit', payload);
      state.playerLimit = payload;
      save({ playerLimit: state.playerLimit });
    },
    updatePlayerPerRow(state, { payload }: PA<number>) {
      logDebug('Store::Settings::updatePlayerPerRow', payload);
      state.playerPerRow = payload;
      save({ playerPerRow: state.playerPerRow });
    },
    updateShowLB(state, { payload }: PA<boolean>) {
      logDebug('Store::Settings::updateShowLB', payload);
      state.showLB = payload;
      save({ showLB: state.showLB });
    },
    updateYouName(state, { payload }: PA<string>) {
      logDebug('Store::Settings::updateYouName', payload);
      state.youName = payload;
      save({ youName: state.youName });
    },
    updatePetMergeID(state, { payload }: PA<string>) {
      logDebug('Store::Settings::updatePetMergeID', payload);
      state.petMergeID = payload;
      save({ petMergeID: state.petMergeID });
    },
    updateShortNumber(state, { payload }: PA<boolean>) {
      logDebug('Store::Settings::updateShortNumber', payload);
      state.shortNumber = payload;
      save({ shortNumber: state.shortNumber });
    },
    // display
    updateDispMode(state, { payload }: PA<DisplayModeMapKey>) {
      logDebug('Store::Settings::updateDispMode', payload);
      state.dispMode = payload;
      save({ dispMode: state.dispMode });
    },
    updateDispContent(state, { payload }: PA<Partial<DispContentSettings>>) {
      logDebug('Store::Settings::updateDispContent', payload);
      state.dispContent = { ...state.dispContent, ...payload };
      save({ dispContent: state.dispContent });
    },
    updateHlYou(state, { payload }: PA<boolean>) {
      logDebug('Store::Settings::updateHlYou', payload);
      state.hlYou = payload;
      save({ hlYou: state.hlYou });
    },
    updateTicker(state, { payload }: PA<Partial<TickerSettings>>) {
      logDebug('Store::Settings::updateTicker', payload);
      state.ticker = { ...state.ticker, ...payload };
      save({ ticker: state.ticker });
    },
    updateTickerAlign(state, { payload }: PA<Partial<TickerAlignSettings>>) {
      logDebug('Store::Settings::updateTickerAlign', payload);
      state.tickerAlign = { ...state.tickerAlign, ...payload };
      save({ tickerAlign: state.tickerAlign });
    },
    updateBottomDisp(state, { payload }: PA<BottomDispMapKey>) {
      logDebug('Store::Settings::updateBottomDisp', payload);
      state.bottomDisp = payload;
      save({ bottomDisp: state.bottomDisp });
    },
    updateShortName(state, { payload }: PA<ShortNameMapKey>) {
      logDebug('Store::Settings::updateShortName', payload);
      state.shortName = payload;
      save({ shortName: state.shortName });
    },
    // general
    updateOpacity(state, { payload }: PA<number>) {
      logDebug('Store::Settings::updateOpacity', payload);
      state.opacity = payload;
      save({ opacity: state.opacity });
    },
    updateLayoutMode(state, { payload }: PA<LayoutModeMapKey>) {
      logDebug('Store::Settings::updateLayoutMode', payload);
      state.layoutMode = payload;
      save({ layoutMode: state.layoutMode });
    },
    updateMock(state, { payload }: PA<boolean>) {
      logDebug('Store::Settings::updateMock', payload);
      state.mock = payload;
      if (payload === true) {
        if (!lazyMocker) {
          import('@/utils/mocker')
            .then((mocker) => {
              lazyMocker = mocker;
              lazyMocker.startMock();
            })
            .catch((e) => {
              logWarn('Store::Settings::updateMock::loadMockerFailed', e);
            });
        } else {
          lazyMocker.startMock();
        }
      } else {
        if (lazyMocker) {
          lazyMocker.stopMock();
        }
      }
      save({ mock: state.mock });
    },
    updateLang(state, { payload }: PA<LangMapKey>) {
      logDebug('Store::Settings::updateLang', payload);
      state.lang = payload;
      save({ lang: state.lang });
    },
    updateZoom(state, { payload }: PA<number>) {
      logDebug('Store::Settings::updateZoom', payload);
      state.zoom = payload;
      save({ zoom: state.zoom });
    },
    updateFonts(state, { payload }: PA<Partial<FontSettings>>) {
      logDebug('Store::Settings::updateFonts', payload);
      state.fonts = { ...state.fonts, ...payload };
      save({ fonts: state.fonts });
    },
    updateCustomCSS(state, { payload }: PA<string>) {
      logDebug('Store::Settings::updateCustomCSS', payload);
      state.customCSS = payload;
      save({ customCSS: state.customCSS });
    },
  },
});

export const {
  toggleShowCombatants,
  toggleSettings,
  toggleBlurName,
  updateSort,
  updatePlayerLimit,
  updatePlayerPerRow,
  updateShowLB,
  updateYouName,
  updatePetMergeID,
  updateShortNumber,
  updateDispMode,
  updateDispContent,
  updateHlYou,
  updateTicker,
  updateTickerAlign,
  updateBottomDisp,
  updateShortName,
  updateOpacity,
  updateLayoutMode,
  updateMock,
  updateLang,
  updateZoom,
  updateFonts,
  updateCustomCSS,
} = settingsSlice.actions;

/** @redux effects */

export const listener = createListenerMiddleware();

// reset font weight if font family changed to incompatible one
listener.startListening({
  actionCreator: updateFonts,
  effect: ({ payload }, api) => {
    const nextFamily = payload.family;
    if (nextFamily) {
      const state = api.getState() as RootState;
      const nextFamilyData = MAP_FONT_FAMILY[nextFamily];
      const currentWeight = state.settings?.fonts?.weight;
      if (
        nextFamilyData &&
        (currentWeight < nextFamilyData.weights[0] || currentWeight > nextFamilyData.weights[1])
      ) {
        logDebug('Listener::Settings::updateFonts::resetFontWeight');
        api.dispatch(updateFonts({ weight: 400 }));
      }
    }
  },
});

// apply dom when settings changed
listener.startListening({
  actionCreator: updateLang,
  effect: ({ payload }) => applyLang(payload),
});
listener.startListening({
  actionCreator: updateFonts,
  effect: ({ payload }) => {
    const { family, weight } = payload;
    family && applyFonts(family);
    weight && applyFontWeight(weight);
  },
});
listener.startListening({
  actionCreator: updateZoom,
  effect: ({ payload }) => applyZoom(payload),
});
listener.startListening({
  actionCreator: updateCustomCSS,
  effect: ({ payload }) => applyCustomCSS(payload),
});

// clean all data if closing mock
listener.startListening({
  actionCreator: updateMock,
  effect: ({ payload }, api) => {
    if (payload === false) {
      api.dispatch(cleanMockData());
    }
  },
});

export default {
  reducer: settingsSlice.reducer,
  middleware: listener.middleware,
};
